import { Helmet } from 'react-helmet-async'
import { Button } from '@/components/ui/button'
import { MessageCircle, Send } from 'lucide-react'

export default function MentoringPage() {
  return (
    <main className="min-h-screen bg-background">
        <Helmet>
          <title>Raintree Mentoring | Graduate Support in Chinese Medicine</title>
          <meta name="description" content="Affordable mentoring for new graduates in Chinese medicine and acupuncture. Join Renee Knott for supportive, practical sessions online or on the Sunshine Coast." />
          <meta property="og:title" content="Raintree Mentoring | Graduate Support in Chinese Medicine" />
          <meta property="og:description" content="Affordable mentoring for new graduates in Chinese medicine and acupuncture. Join Renee Knott for supportive, practical sessions online or on the Sunshine Coast." />
          <meta property="og:type" content="website" />
          <meta property="og:url" content="https://raintreechinesemedicine.com.au/mentoring" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Raintree Mentoring | Graduate Support in Chinese Medicine" />
          <meta name="twitter:description" content="Affordable mentoring for new graduates in Chinese medicine and acupuncture. Join Renee Knott for supportive, practical sessions online or on the Sunshine Coast." />
        </Helmet>

      {/* Page container */}
      <div className="container mx-auto px-4 py-16 lg:py-24 max-w-screen-lg">
        {/* Hero */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div className="order-2 md:order-1">
            <h1 className="text-3xl sm:text-4xl lg:text-7xl tracking-tight text-foreground" style={{ fontFamily: "'Avenir Next', 'Inter', sans-serif", fontWeight: 600 }}>
              Raintree Mentoring
            </h1>
            <h2 className="mt-3 text-lg sm:text-xl text-muted-foreground" style={{ fontFamily: "'Avenir Next', 'Inter', sans-serif" }}>
              From graduation to grounded practitioner.
            </h2>

            {/* CTAs */}
            <div className="mt-6 md:mt-28 flex flex-col sm:flex-row gap-3">
              <Button asChild className="sm:w-auto w-full">
                <a href="mailto:renee@raintreechinesemedicine.com.au" aria-label="Email Renee about mentoring">
                  <span className="inline-flex items-center gap-2">
                    <Send className="h-4 w-4" aria-hidden="true" />
                    Email Renee
                  </span>
                </a>
              </Button>
              <Button asChild variant="outline" className="sm:w-auto w-full">
                <a href="https://wa.me/61422034039" target="_blank" rel="noopener noreferrer" aria-label="Message Renee on WhatsApp">
                  <span className="inline-flex items-center gap-2">
                    <MessageCircle className="h-4 w-4" aria-hidden="true" />
                    Message on WhatsApp
                  </span>
                </a>
              </Button>
            </div>

            <div className="mt-4 md:mt-4">
            <p className="text-sm text-muted-foreground">
              In person on the Sunshine Coast or via Zoom.
            </p>    
            </div>
          </div>

          {/* Hero Image */}
          <div className="relative order-1 md:order-2">
            <div className="rounded-xl overflow-hidden">
              <img
                src="https://tdjwqdpgnmzhfcqsfyjh.supabase.co/storage/v1/object/sign/images/general/Raintree-Chinese-Medicine-Mentoring.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8wYzVlMGM1NS1lYzI2LTRkMTYtOTc2MS1iZDQ1MWJhMGJiYjEiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJpbWFnZXMvZ2VuZXJhbC9SYWludHJlZS1DaGluZXNlLU1lZGljaW5lLU1lbnRvcmluZy5wbmciLCJpYXQiOjE3NjIzMDIzNTAsImV4cCI6NDg4NDM2NjM1MH0.41ajzIBbrXGWyoj2ilyJfg8gc81s9aH0FgXYxqaq01k"
                alt="Renee Knott — Raintree Mentoring for new graduates."
                className="w-full h-auto object-contain"
                onError={(e) => {
                  const target = e.currentTarget as HTMLImageElement
                  target.style.display = 'none'
                }}
              />
            </div>
          </div>
        </section>

        {/* Intro Paragraph */}
        <section className="mt-16 lg:mt-24">
        <h3 className="text-xl font-semibold text-foreground mb-6">Raintree Mentoring with Renee</h3>
          <div className="prose prose-neutral max-w-none">
            <p className="text-base leading-7 text-foreground">
            Guided by Renee’s 20+ years of clinical experience in Chinese medicine, Raintree Mentoring offers new graduates a supportive bridge into real practice. Each session blends the practical and the personal — exploring herbs, acupuncture, and the flow of clinical decision-making. Whether weekly, fortnightly, or monthly, these affordable mentoring sessions help you grow confidence, refine your craft, and feel steady as you begin your own path in practice.
            </p>
          </div>
        </section>

        {/* How It Works */}
        <section className="mt-12 lg:mt-16">
          <h3 className="text-xl font-semibold text-foreground mb-6">How It Works</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-lg border bg-card p-4">
              <div className="text-sm text-muted-foreground">Format</div>
              <div className="mt-1 text-foreground">In person on the Sunshine Coast or online via Zoom</div>
            </div>
            <div className="rounded-lg border bg-card p-4">
              <div className="text-sm text-muted-foreground">Frequency</div>
              <div className="mt-1 text-foreground">Weekly, fortnightly, or monthly</div>
            </div>
            <div className="rounded-lg border bg-card p-4">
              <div className="text-sm text-muted-foreground">Duration</div>
              <div className="mt-1 text-foreground">1 hour per session</div>
            </div>
            <div className="rounded-lg border bg-card p-4">
              <div className="text-sm text-muted-foreground">Investment</div>
              <div className="mt-1 text-foreground">$50 (1 person) / $70 (2–3 people)</div>
            </div>
            <div className="rounded-lg border bg-card p-4 md:col-span-2">
              <div className="text-sm text-muted-foreground">Focus areas</div>
              <div className="mt-1 text-foreground">Case review, treatment planning, herbal formulas, patient communication, business setup</div>
            </div>
            <div className="rounded-lg border bg-card p-4 md:col-span-2">
              <div className="text-sm text-muted-foreground">Preparation</div>
              <div className="mt-1 text-foreground">Send your questions before each session so we can dive straight in</div>
            </div>
          </div>
        </section>

        {/* Why Mentoring Matters */}
        <section className="mt-12 lg:mt-16">
          <h3 className="text-xl font-semibold text-foreground mb-4">Why Mentoring Matters</h3>
          <p className="text-base leading-7 text-foreground">
            Starting your own practice can feel both exciting and overwhelming. Mentoring provides a space to stay connected, ask questions, and build confidence in your clinical decisions. With consistent support from Renee, you’ll feel more grounded in your understanding of herbs, acupuncture, and patient care — and more inspired in your growth as a practitioner.
          </p>
        </section>

        {/* CTA Footer */}
        <section className="mt-16 lg:mt-24">
          <div className="rounded-xl bg-muted/30 p-8 text-center">
            <h4 className="text-lg sm:text-xl font-semibold text-foreground">Ready to grow your confidence in practice?</h4>
            <div className="mt-4 flex flex-col sm:flex-row gap-3 justify-center">
              <Button asChild className="sm:w-auto w-full"> 
                <a href="mailto:renee@raintreechinesemedicine.com.au" aria-label="Email Renee about mentoring">
                  <span className="inline-flex items-center gap-2">
                    <Send className="h-4 w-4" aria-hidden="true" />
                    Email Renee
                  </span>
                </a>
              </Button>
              <Button asChild variant="outline" className="sm:w-auto w-full">
                <a href="https://wa.me/61422034039" target="_blank" rel="noopener noreferrer" aria-label="Message Renee on WhatsApp">
                  <span className="inline-flex items-center gap-2">
                    <MessageCircle className="h-4 w-4" aria-hidden="true" />
                    Message on WhatsApp
                  </span>
                </a>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </main>
  )
}


